import React, { useState } from "react";
import { Mail, ArrowUpRight, Copy, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { HR_EMAIL } from "../data/mock";

const SendResumeCTA = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const openGmail = () => {
    const subject = encodeURIComponent("Resume Submission - Learn Overseas Careers");
    const body = encodeURIComponent(
      "Dear Hiring Team,\n\nPlease find my resume attached for your consideration.\n\nThank you,"
    );
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${HR_EMAIL}&su=${subject}&body=${body}`;
    window.open(gmailUrl, "_blank", "noopener,noreferrer");
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(HR_EMAIL);
      setCopied(true);
      toast({ title: "Email copied!", description: HR_EMAIL });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Please copy manually: " + HR_EMAIL });
    }
  };

  return (
    <section className="relative w-full pb-20 lg:pb-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="relative rounded-2xl bg-[#FFF4E6] px-6 sm:px-10 py-7 sm:py-9 flex flex-col sm:flex-row items-start sm:items-center gap-6 overflow-hidden">
          {/* dashed plane trail */}
          <svg
            className="absolute right-6 top-1/2 -translate-y-1/2 hidden md:block"
            width="260"
            height="80"
            viewBox="0 0 260 80"
            fill="none"
            aria-hidden
          >
            <path
              d="M5 60 C 60 30, 120 30, 180 50"
              stroke="#FFB37A"
              strokeWidth="1.5"
              strokeDasharray="4 6"
              fill="none"
            />
          </svg>

          <div className="flex items-center gap-5 flex-1 min-w-0 z-10">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white flex items-center justify-center shadow-sm ring-1 ring-orange-100 flex-shrink-0">
              <Mail className="w-6 h-6 text-[#FF7A1A]" strokeWidth={2.2} />
            </div>
            <div>
              <h3 className="font-display text-2xl sm:text-[26px] font-extrabold text-slate-900">
                Don’t see the right role?
              </h3>
              <p className="mt-1 text-slate-500 text-[14px]">
                Send us your resume and we’ll keep you in mind for future
                opportunities.
              </p>
            </div>
          </div>

          {/* paper plane decoration */}
          <div className="absolute right-10 top-6 plane-float z-0 hidden md:block">
            <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
              <path
                d="M58 8 L34 30 L18 26 L12 32 L28 38 L26 44 L34 46 L36 52 L42 38 L58 8 Z"
                fill="#FF7A1A"
              />
            </svg>
          </div>

          <div className="z-10">
            <button
              onClick={() => setOpen(true)}
              className="btn-primary inline-flex items-center gap-2 rounded-xl px-6 py-4 text-[15px] font-semibold"
            >
              Send Your Resume
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Send your resume
            </DialogTitle>
            <DialogDescription>
              Email your resume to our HR team. Open Gmail directly or copy the
              email address.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 flex items-center justify-between gap-2 rounded-xl bg-orange-50 border border-orange-100 px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <Mail className="w-5 h-5 text-[#FF7A1A] flex-shrink-0" strokeWidth={2.2} />
              <span className="font-medium text-slate-900 truncate">
                {HR_EMAIL}
              </span>
            </div>
            <button
              onClick={copyEmail}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#FF7A1A] hover:text-orange-600 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" /> Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> Copy
                </>
              )}
            </button>
          </div>

          <DialogFooter className="mt-2 gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button
              onClick={openGmail}
              className="bg-[#FF7A1A] hover:bg-[#F36A0E] text-white"
            >
              Open Gmail
              <ArrowUpRight className="ml-1 w-4 h-4" strokeWidth={2.5} />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default SendResumeCTA;
